// src/.../components/summary-cards/SummaryCard.tsx
import React from "react";
import { LocalAtm, Receipt, Restaurant, Payment, TrendingUp, TrendingDown, LocalShipping } from "@mui/icons-material";
import { useUser } from "src/shared/context/UserContext";

export type SummaryCardIcon = "localAtm" | "receipt" | "restaurant" | "payment" | "localShipping";

export interface SummaryCardProps {
  title: string;
  value: number | string;
  icon: SummaryCardIcon;
  previousValue?: number;
}

const ICONS = {
  localAtm: LocalAtm,
  receipt: Receipt,
  restaurant: Restaurant,
  payment: Payment,
  localShipping: LocalShipping,
};

const COLOR_SCHEMES = {
  localAtm: {
    bg: "from-green-50 to-green-100",
    iconBg: "bg-green-500",
    text: "text-green-700",
  },
  receipt: {
    bg: "from-blue-50 to-blue-100",
    iconBg: "bg-blue-500",
    text: "text-blue-700",
  },
  restaurant: {
    bg: "from-orange-50 to-orange-100",
    iconBg: "bg-orange-500",
    text: "text-orange-700",
  },
  payment: {
    bg: "from-purple-50 to-purple-100",
    iconBg: "bg-purple-500",
    text: "text-purple-700",
  },
  localShipping: {
    bg: "from-indigo-50 to-indigo-100",
    iconBg: "bg-indigo-500",
    text: "text-indigo-700",
  },
};

const getTrendData = (value: number, previousValue?: number) => {
  if (!previousValue || previousValue === 0) return { trend: "neutral", change: 0 };
  const change = ((value - previousValue) / previousValue) * 100;
  const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";
  return { trend, change: Math.abs(Number(change.toFixed(1))) };
};

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, icon, previousValue }) => {
  const { userConfigration } = useUser();
  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);

  if (icon === "localShipping" && !deliveryFeeEnabled) {
    return null;
  }

  const Icon = ICONS[icon];
  const color = COLOR_SCHEMES[icon];
  const { trend, change } = getTrendData(Number(value), previousValue);

  const trendStyle =
    trend === "up"
      ? { bg: "bg-green-100", text: "text-green-700", icon: <TrendingUp className="w-4 h-4" /> }
      : trend === "down"
        ? { bg: "bg-red-100", text: "text-red-700", icon: <TrendingDown className="w-4 h-4" /> }
        : { bg: "bg-gray-100", text: "text-gray-500", icon: null };

  // Ensure consistent minimum height
  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-6 shadow-md border border-gray-100 
      bg-gradient-to-br ${color.bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1
      min-h-[160px] flex flex-col justify-between`}
    >
      {/* 🔹 Title + Icon on the same line */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${color.iconBg} text-white`}>
            <Icon className="w-5 h-5" />
          </div>
          <h3 className={`text-base font-semibold ${color.text} uppercase tracking-wide`}>{title}</h3>
        </div>

        {trend !== "neutral" && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold 
              ${trendStyle.bg} ${trendStyle.text}`}
          >
            {trendStyle.icon}
            <span>
              {trend === "up" ? "+" : ""}
              {change}%
            </span>
          </div>
        )}
      </div>

      {/* 🔹 Value - flex grow to push to bottom if needed */}
      <div className="mt-4">
        <p className="text-3xl font-bold text-gray-900 tracking-tight">{typeof value === "number" ? value.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : value}</p>
      </div>

      {/* 🔹 Accent bar */}
      <div className={`absolute bottom-0 left-0 w-full h-1.5 ${color.iconBg} opacity-40 rounded-b-2xl`} />
    </div>
  );
};

export default SummaryCard;
